import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { peekSessionExpired, stashReturnPath } from '@/lib/return-path';

/**
 * The full-screen spinner shown while the boot session check runs. Shared by
 * `RequireAuth` and the "/" route (#2657) so the words are translated once.
 */
export function SessionLoading() {
  const { t } = useTranslation('common');
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-stevens-ink-3">{t('states.loading')}</p>
      </div>
    </div>
  );
}

/**
 * The calm "can't reach TRF" state (#2641): the boot check could not answer
 * (5xx, network) and nobody is adopted. The provider keeps retrying /auth/me;
 * Try again retries now. Shared by `RequireAuth` and the "/" route (#2657),
 * which otherwise showed the public landing page to a signed-in family.
 */
export function SessionUnreachable() {
  const { retrySessionCheck } = useAuth();
  const { t } = useTranslation('auth');
  return (
    <div className="flex items-center justify-center min-h-screen px-4" data-testid="session-unreachable">
      <div className="text-center max-w-[36ch] space-y-4">
        <p role="status" className="text-body text-stevens-ink-2">{t('shared.unreachable')}</p>
        <Button
          type="button"
          variant="outline"
          className="[@media(pointer:coarse)]:min-h-11"
          onClick={() => retrySessionCheck()}
        >
          {t('shared.unreachableRetry')}
        </Button>
      </div>
    </div>
  );
}

/**
 * The signed-out guard for every app route (was `ProtectedRoute` in App.tsx).
 *
 * #2429: it used to send a signed-out visitor to a bare /login, so the next
 * sign-in could only land on the role's home — a parent whose session ended
 * on /chat came back to Today. It now stashes where she was (consumed by the
 * sign-in completions in AuthContext) and passes it as router state too.
 *
 * When the provider saw a live session end under her (`markSessionExpired`),
 * /login also gets `reason: 'expired'` — the page says why she is there — and
 * `?lang=` with her profile language, so the sign-in page is one she can read.
 *
 * #2641: a boot check that could not answer (a 5xx during a deploy restart, a
 * network drop) is not a sign-out. While `sessionCheckFailed` holds and no one
 * is adopted, the guard shows a calm "can't reach TRF" state with Try again;
 * the provider keeps retrying /auth/me and, once it answers, the page renders
 * (or, on a real 401, the sign-in redirect below runs). One guard covers the
 * parent shell, the staff shell and the TRFer mobile shell.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading, sessionCheckFailed } = useAuth();
  const { pathname, search, hash } = useLocation();

  if (loading) return <SessionLoading />;

  if (!isAuthenticated && sessionCheckFailed) return <SessionUnreachable />;

  if (!isAuthenticated) {
    const from = `${pathname}${search}${hash}`;
    const expired = peekSessionExpired();
    // Idempotent: a re-render writes the same entry again.
    stashReturnPath(from, expired?.account ?? null);
    const state = expired ? { from, reason: 'expired' as const } : { from };
    const to = { pathname: '/login', search: expired?.lang ? `?lang=${encodeURIComponent(expired.lang)}` : '' };
    return <Navigate to={to} replace state={state} />;
  }

  return <>{children}</>;
}
